/**
 * Provider Form Component
 * Reusable form for creating and editing Provider data with sequential tabs
 */
import React, { useState, useMemo, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {
  Box,
  Grid,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Typography,
  Card,
  CardContent,
  FormHelperText,
  InputAdornment,
  IconButton,
  Alert,
  Stepper,
  Step,
  StepLabel,
} from '@mui/material';
import {
  Save as SaveIcon,
  Cancel as CancelIcon,
  Search as SearchIcon,
  NavigateNext as NextIcon,
  NavigateBefore as BackIcon,
  CloudUpload as UploadIcon,
  Delete as DeleteIcon,
  InsertDriveFile as FileIcon,
} from '@mui/icons-material';
import { EstagioDoPrestador, Provider, ProviderStatus, ProviderType, ServiceType, ProviderDocumentType, PROVIDER_DOCUMENT_TYPE_LABELS, ProviderDocument } from '../../../types/provider';
import { PrerequisiteBlock } from '../../../components/common';
import { CreateProviderRequest, providerService } from '../../../services/providerService';
import ProviderWeeklyScheduleModal from './ProviderWeeklyScheduleModal';
import { CalendarMonth as CalendarIcon } from '@mui/icons-material';
import { companyService } from '../../../services/companyService';

// Utility functions for CNPJ formatting
const formatCNPJ = (value: string | null | undefined): string => {
  if (!value) return '';
  const digits = value.replace(/\D/g, '').slice(0, 14);
  if (digits.length <= 2) return digits;
  if (digits.length <= 5) return `${digits.slice(0, 2)}.${digits.slice(2)}`;
  if (digits.length <= 8) return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5)}`;
  if (digits.length <= 12) return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8)}`;
  return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8, 12)}-${digits.slice(12)}`;
};

const removeCNPJMask = (value: string): string => value.replace(/\D/g, '');

const formatCEP = (value: string): string => {
  const digits = value.replace(/\D/g, '').slice(0, 8);
  if (digits.length <= 5) return digits;
  return `${digits.slice(0, 5)}-${digits.slice(5)}`;
};

const removeCEPMask = (value: string): string => value.replace(/\D/g, '');

// Tab steps are now computed dynamically inside the component via useMemo

// Document state type
interface DocFieldState {
  documentNumber: string;
  validityDate: string;
  fileName: string;
  uploading: boolean;
  file?: File;
}

const CERTIFICATE_TYPES: ProviderDocumentType[] = [
  ProviderDocumentType.LO,
  ProviderDocumentType.AF,
  ProviderDocumentType.AVCB,
  ProviderDocumentType.IBAMA,
  ProviderDocumentType.CTF,
  ProviderDocumentType.CETESB,
];

const emptyDocState = (): Record<ProviderDocumentType, DocFieldState> => {
  const state: any = {};
  for (const type of Object.values(ProviderDocumentType)) {
    state[type] = { documentNumber: '', validityDate: '', fileName: '', uploading: false };
  }
  return state;
};

// Define validation schemas for each tab (commented out - not currently used)
// const basicDataSchema = yup.object({
//   legalName: yup.string().required('Razão Social é obrigatória').min(2, 'Mínimo de 2 caracteres').max(255, 'Máximo de 255 caracteres'),
//   cnpj: yup
//     .string()
//     .required('CNPJ é obrigatório')
//     .test('cnpj-format', 'CNPJ deve conter 14 dígitos', (value) => {
//       if (!value) return false;
//       const numericOnly = value.replace(/\D/g, '');
//       return numericOnly.length === 14;
//     }),
//   providerType: yup.string().oneOf(Object.values(ProviderType) as string[], 'Tipo de Prestador inválido').required('Tipo de Prestador é obrigatório'),
//   tradeName: yup.string().max(255, 'Máximo de 255 caracteres'),
//   status: yup.string().oneOf(Object.values(ProviderStatus) as string[], 'Status inválido').default(ProviderStatus.ATIVO),
// });

// const addressSchema = yup.object({
//   zipCode: yup.string()
//     .test('cep-format', 'CEP deve ter 8 dígitos', (value) => {
//       if (!value) return true; // optional field
//       const numericOnly = value.replace(/\D/g, '');
//       return numericOnly.length === 8;
//     })
//     .nullable()
//     .transform(value => value === '' ? null : value),
//   address: yup.string().max(255, 'Máximo de 255 caracteres').nullable().transform(value => value === '' ? null : value),
//   addressNumber: yup.string().max(20, 'Máximo de 20 caracteres').nullable().transform(value => value === '' ? null : value),
//   neighborhood: yup.string().max(100, 'Máximo de 100 caracteres').nullable().transform(value => value === '' ? null : value),
//   city: yup.string().max(100, 'Máximo de 100 caracteres').nullable().transform(value => value === '' ? null : value),
//   state: yup.string().matches(/^[A-Z]{2}$/, 'UF deve ter 2 caracteres maiúsculos').nullable().transform(value => value === '' ? null : value),
// });

// const contactsSchema = yup.object({
//   receptionContactName: yup.string().max(255, 'Máximo de 255 caracteres').nullable().transform(value => value === '' ? null : value),
//   receptionEmail: yup.string().email('E-mail de recepção inválido').max(255, 'Máximo de 255 caracteres').nullable().transform(value => value === '' ? null : value),
//   receptionPhone: yup.string().max(20, 'Máximo de 20 caracteres').nullable().transform(value => value === '' ? null : value),
//   reportContactName: yup.string().max(255, 'Máximo de 255 caracteres').nullable().transform(value => value === '' ? null : value),
//   reportEmail: yup.string().email('E-mail de laudos inválido').max(255, 'Máximo de 255 caracteres').nullable().transform(value => value === '' ? null : value),
//   reportPhone: yup.string().max(20, 'Máximo de 20 caracteres').nullable().transform(value => value === '' ? null : value),
//   contactName: yup.string().max(255, 'Máximo de 255 caracteres').nullable().transform(value => value === '' ? null : value),
//   contactEmail: yup.string().email('E-mail administrativo inválido').max(255, 'Máximo de 255 caracteres').nullable().transform(value => value === '' ? null : value),
//   contactPhone: yup.string().max(20, 'Máximo de 20 caracteres').nullable().transform(value => value === '' ? null : value),
// });

// const financialSchema = yup.object({
//   bankName: yup.string().max(100, 'Máximo de 100 caracteres').nullable().transform(value => value === '' ? null : value),
//   bankAgency: yup.string().max(20, 'Máximo de 20 caracteres').nullable().transform(value => value === '' ? null : value),
//   bankAccount: yup.string().max(30, 'Máximo de 30 caracteres').nullable().transform(value => value === '' ? null : value),
//   paymentMethod: yup.string().max(50, 'Máximo de 50 caracteres').nullable().transform(value => value === '' ? null : value),
// });

// Full validation schema (combines all tabs)
const validationSchema = yup.object({
  legalName: yup.string().required('Razão Social é obrigatória').min(2, 'Mínimo de 2 caracteres').max(255, 'Máximo de 255 caracteres'),
  cnpj: yup
    .string()
    .required('CNPJ é obrigatório')
    .test('cnpj-format', 'CNPJ deve conter 14 dígitos', (value) => {
      if (!value) return false;
      const numericOnly = value.replace(/\D/g, '');
      return numericOnly.length === 14;
    }),
  providerType: yup.string().oneOf(Object.values(ProviderType) as string[], 'Tipo de Prestador inválido').required('Tipo de Prestador é obrigatório'),
  tradeName: yup.string().max(255, 'Máximo de 255 caracteres'),
  status: yup.string().oneOf(Object.values(ProviderStatus) as string[], 'Status inválido').default(ProviderStatus.ATIVO),
  
  // Address fields
  zipCode: yup.string()
    .test('cep-format', 'CEP deve ter 8 dígitos', (value) => {
      if (!value) return true; // optional field
      const numericOnly = value.replace(/\D/g, '');
      return numericOnly.length === 8;
    })
    .nullable()
    .transform(value => value === '' ? null : value),
  address: yup.string().max(255, 'Máximo de 255 caracteres').nullable().transform(value => value === '' ? null : value),
  addressNumber: yup.string().max(20, 'Máximo de 20 caracteres').nullable().transform(value => value === '' ? null : value),
  neighborhood: yup.string().max(100, 'Máximo de 100 caracteres').nullable().transform(value => value === '' ? null : value),
  city: yup.string().max(100, 'Máximo de 100 caracteres').nullable().transform(value => value === '' ? null : value),
  state: yup.string().matches(/^[A-Z]{2}$/, 'UF deve ter 2 caracteres maiúsculos').nullable().transform(value => value === '' ? null : value),

  // Contact fields (reception, report, admin)
  receptionContactName: yup.string().max(255, 'Máximo de 255 caracteres').nullable().transform(value => value === '' ? null : value),
  receptionEmail: yup.string().email('E-mail de recepção inválido').max(255, 'Máximo de 255 caracteres').nullable().transform(value => value === '' ? null : value),
  receptionPhone: yup.string().max(20, 'Máximo de 20 caracteres').nullable().transform(value => value === '' ? null : value),

  reportContactName: yup.string().max(255, 'Máximo de 255 caracteres').nullable().transform(value => value === '' ? null : value),
  reportEmail: yup.string().email('E-mail de laudos inválido').max(255, 'Máximo de 255 caracteres').nullable().transform(value => value === '' ? null : value),
  reportPhone: yup.string().max(20, 'Máximo de 20 caracteres').nullable().transform(value => value === '' ? null : value),

  contactName: yup.string().max(255, 'Máximo de 255 caracteres').nullable().transform(value => value === '' ? null : value),
  contactEmail: yup.string().email('E-mail administrativo inválido').max(255, 'Máximo de 255 caracteres').nullable().transform(value => value === '' ? null : value),
  contactPhone: yup.string().max(20, 'Máximo de 20 caracteres').nullable().transform(value => value === '' ? null : value),

  // Financial fields
  bankName: yup.string().max(100, 'Máximo de 100 caracteres').nullable().transform(value => value === '' ? null : value),
  bankAgency: yup.string().max(20, 'Máximo de 20 caracteres').nullable().transform(value => value === '' ? null : value),
  bankAccount: yup.string().max(30, 'Máximo de 30 caracteres').nullable().transform(value => value === '' ? null : value),
  paymentMethod: yup.string().max(50, 'Máximo de 50 caracteres').nullable().transform(value => value === '' ? null : value),
  pix: yup.string().max(100, 'Máximo de 100 caracteres').nullable().transform(value => value === '' ? null : value),
  contractStartDate: yup.string().nullable().transform(value => value === '' ? null : value),
  contractEndDate: yup.string().nullable().transform(value => value === '' ? null : value),
  closingDate: yup.string().nullable().transform(value => value === '' ? null : value),
  paymentDate: yup.string().nullable().transform(value => value === '' ? null : value),
  // Reajuste de valores dos exames (aplicação do percentual a ser desenvolvida posteriormente)
  priceAdjustmentDate: yup.string().nullable().transform(value => value === '' ? null : value),
  priceAdjustmentPercentage: yup
    .number()
    .typeError('Percentual deve ser um número')
    .min(0, 'Percentual não pode ser negativo')
    .max(999.99, 'Percentual máximo é 999,99')
    .nullable()
    .transform((value, originalValue) => (originalValue === '' || originalValue === null ? null : value)),
  terminationReason: yup.string().max(255, 'Máximo de 255 caracteres').nullable().transform(value => value === '' ? null : value),
  financialContactName: yup.string().max(255, 'Máximo de 255 caracteres').nullable().transform(value => value === '' ? null : value),
  financialEmail: yup.string().email('E-mail financeiro inválido').max(255, 'Máximo de 255 caracteres').nullable().transform(value => value === '' ? null : value),
  financialPhone: yup.string().max(20, 'Máximo de 20 caracteres').nullable().transform(value => value === '' ? null : value),
  // firstContactDate is not included here as it might be handled by date pickers or backend default

  // Attendance fields
  serviceType: yup.string().oneOf(Object.values(ServiceType) as string[], 'Tipo de Atendimento inválido').required('Tipo de Atendimento é obrigatório'),
  providerNotice: yup.string().nullable().transform(value => value === '' ? null : value),

  // Document fields
  responsibleName: yup.string().max(255, 'Máximo de 255 caracteres').nullable().transform(value => value === '' ? null : value),
  responsibleCRM: yup.string().max(50, 'Máximo de 50 caracteres').nullable().transform(value => value === '' ? null : value),
}) as yup.ObjectSchema<CreateProviderRequest>;

/**
 * A clínica prospectada salva sem CNPJ: corrigir o endereço de uma clínica que a integração
 * trouxe não deve exigir descobrir o número na hora. Informado, o CNPJ continua conferido — e é
 * ele que a promove a prestador.
 */
const validationSchemaDaProspectada = validationSchema.shape({
  cnpj: yup.string().test('cnpj-format', 'CNPJ deve conter 14 dígitos', (value) => {
    if (!value) return true;
    return value.replace(/\D/g, '').length === 14;
  }),
  // Pelo mesmo motivo do CNPJ: a integração não traz como a clínica atende, e exigir isso para
  // salvar recriaria a barreira que a exceção do CNPJ acabou de remover.
  serviceType: yup
    .string()
    .transform((value) => (value === '' ? undefined : value))
    .oneOf(Object.values(ServiceType) as string[], 'Tipo de Atendimento inválido'),
}) as yup.ObjectSchema<CreateProviderRequest>;

interface ProviderFormProps {
  initialData?: Provider; // Data to pre-fill for editing
  providerId?: string; // For upload in edit mode
  initialDocuments?: ProviderDocument[]; // Pre-loaded documents
  onSubmit: (data: any) => void;
  onCancel: () => void;
  isSubmitting: boolean;
}

const ProviderForm: React.FC<ProviderFormProps> = ({ initialData, providerId, initialDocuments, onSubmit, onCancel, isSubmitting }) => {
  const ehClinicaProspectada = initialData?.estagio === EstagioDoPrestador.EMBRIONARIO;
  // Depois que o prestador está completo o CNPJ não muda mais. O campo trava em vez de aceitar a
  // digitação e recusar no salvamento, que faria a pessoa perder o preenchimento sem entender.
  const cnpjImutavel = Boolean(initialData) && !ehClinicaProspectada;
  const [currentTab, setCurrentTab] = useState(0);
  const [weeklyScheduleOpen, setWeeklyScheduleOpen] = useState(false);
  const [cnpjLookupLoading, setCnpjLookupLoading] = useState(false);
  const [cnpjLookupError, setCnpjLookupError] = useState<string | null>(null);
  const [cnpjLookupSuccess, setCnpjLookupSuccess] = useState<string | null>(null);
  const [docState, setDocState] = useState<Record<ProviderDocumentType, DocFieldState>>(() => {
    const state = emptyDocState();
    if (initialDocuments) {
      for (const doc of initialDocuments) {
        if (state[doc.documentType]) {
          state[doc.documentType] = {
            documentNumber: doc.documentNumber || '',
            validityDate: doc.validityDate || '',
            fileName: doc.originalName || doc.fileName || '',
            uploading: false,
          };
        }
      }
    }
    return state;
  });

  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue,
    getValues,
    trigger,
    watch,
  } = useForm<CreateProviderRequest>({
    resolver: yupResolver(ehClinicaProspectada ? validationSchemaDaProspectada : validationSchema),
    mode: 'onBlur',
    defaultValues: initialData ? {
      legalName: initialData.legalName,
      cnpj: formatCNPJ(initialData.cnpj),
      providerType: initialData.providerType,
      status: initialData.status,
      tradeName: initialData.tradeName,
      zipCode: initialData.zipCode ? formatCEP(initialData.zipCode) : undefined,
      address: initialData.address,
      addressNumber: initialData.addressNumber,
      neighborhood: initialData.neighborhood,
      city: initialData.city,
      state: initialData.state,
      receptionContactName: initialData.receptionContactName,
      receptionEmail: initialData.receptionEmail,
      receptionPhone: initialData.receptionPhone,
      reportContactName: initialData.reportContactName,
      reportEmail: initialData.reportEmail,
      reportPhone: initialData.reportPhone,
      contactName: initialData.contactName,
      contactEmail: initialData.contactEmail,
      contactPhone: initialData.contactPhone,
      bankName: initialData.bankName,
      bankAgency: initialData.bankAgency,
      bankAccount: initialData.bankAccount,
      paymentMethod: initialData.paymentMethod,
      pix: initialData.pix,
      contractStartDate: initialData.contractStartDate ? String(initialData.contractStartDate).slice(0, 10) : undefined,
      contractEndDate: initialData.contractEndDate ? String(initialData.contractEndDate).slice(0, 10) : undefined,
      closingDate: initialData.closingDate ? String(initialData.closingDate).slice(0, 10) : undefined,
      paymentDate: initialData.paymentDate ? String(initialData.paymentDate).slice(0, 10) : undefined,
      priceAdjustmentDate: initialData.priceAdjustmentDate ? String(initialData.priceAdjustmentDate).slice(0, 10) : undefined,
      priceAdjustmentPercentage: initialData.priceAdjustmentPercentage ?? undefined,
      terminationReason: initialData.terminationReason,
      financialContactName: initialData.financialContactName,
      financialEmail: initialData.financialEmail,
      financialPhone: initialData.financialPhone,
      firstContactDate: initialData.firstContactDate,
      serviceType: initialData.serviceType || '',
      morningStartTime: initialData.morningStartTime || '',
      morningEndTime: initialData.morningEndTime || '',
      morningCapacity: initialData.morningCapacity ?? undefined,
      afternoonStartTime: initialData.afternoonStartTime || '',
      afternoonEndTime: initialData.afternoonEndTime || '',
      afternoonCapacity: initialData.afternoonCapacity ?? undefined,
      concurrentCapacity: initialData.concurrentCapacity ?? undefined,
      defaultSlotDuration: initialData.defaultSlotDuration ?? 30,
      providerNotice: initialData.providerNotice || '',
      responsibleName: initialData.responsibleName || '',
      responsibleCRM: initialData.responsibleCRM || '',
    } : {
      legalName: '',
      cnpj: '',
      providerType: ProviderType.CLINICA,
      status: ProviderStatus.ATIVO,
    },
  });

  // Dynamic tab steps based on serviceType and providerType
  const serviceType = watch('serviceType');
  const providerType = watch('providerType');

  const TAB_STEPS = useMemo(() => {
    const base: { label: string; key: string }[] = [
      { label: 'Dados Principais', key: 'main' },
      { label: 'Endereço', key: 'address' },
      { label: 'Contatos', key: 'contacts' },
      { label: 'Dados Financeiros', key: 'financial' },
    ];
    if (serviceType === ServiceType.ORDEM_CHEGADA || serviceType === ServiceType.AGENDA_CLINICA) {
      base.splice(1, 0, { label: 'Configuração de Atendimento', key: 'attendance' });
    }
    if (providerType === ProviderType.CLINICA || providerType === ProviderType.AMBIENTAL) {
      base.push({ label: 'Documentos', key: 'docs' });
    }
    return base;
  }, [serviceType, providerType]);

  // Protect currentTab if the attendance step disappears
  useEffect(() => {
    if (currentTab >= TAB_STEPS.length) {
      setCurrentTab(TAB_STEPS.length - 1);
    }
  }, [TAB_STEPS.length, currentTab]);

  // Get fields to validate for current tab (key-based)
  const getTabFields = (tabIndex: number): (keyof CreateProviderRequest)[] => {
    const key = TAB_STEPS[tabIndex]?.key;
    switch (key) {
      case 'main':
        return ['legalName', 'cnpj', 'providerType', 'tradeName', 'status', 'serviceType', 'providerNotice'];
      case 'attendance':
        if (serviceType === ServiceType.AGENDA_CLINICA) {
          return ['morningStartTime', 'morningEndTime', 'afternoonStartTime', 'afternoonEndTime'];
        }
        return ['morningStartTime', 'morningEndTime', 'morningCapacity', 'afternoonStartTime', 'afternoonEndTime', 'afternoonCapacity'];
      case 'address':
        return ['zipCode', 'address', 'addressNumber', 'neighborhood', 'city', 'state'];
      case 'contacts':
        return ['receptionContactName', 'receptionEmail', 'receptionPhone', 'reportContactName', 'reportEmail', 'reportPhone', 'contactName', 'contactEmail', 'contactPhone'];
      case 'financial':
        return ['bankName', 'bankAgency', 'bankAccount', 'paymentMethod', 'pix', 'contractStartDate', 'contractEndDate', 'closingDate', 'paymentDate', 'priceAdjustmentDate', 'priceAdjustmentPercentage', 'terminationReason', 'financialContactName', 'financialEmail', 'financialPhone'];
      case 'docs':
        return ['responsibleName', 'responsibleCRM'];
      default:
        return [];
    }
  };

  // Validate current tab before moving to next
  const validateCurrentTab = async (): Promise<boolean> => {
    const fieldsToValidate = getTabFields(currentTab);
    const result = await trigger(fieldsToValidate);
    return result;
  };

  // Handle next button click
  const handleNext = async () => {
    const isValid = await validateCurrentTab();
    if (isValid && currentTab < TAB_STEPS.length - 1) {
      setCurrentTab(currentTab + 1);
      // Scroll to top
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Handle back button click
  const handleBack = () => {
    if (currentTab > 0) {
      setCurrentTab(currentTab - 1);
      // Scroll to top
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Handle CNPJ lookup
  const handleCNPJLookup = async () => {
    const cnpjValue = getValues('cnpj');
    const cleanCnpj = removeCNPJMask(cnpjValue || '');
    
    if (!cleanCnpj || cleanCnpj.length !== 14) {
      setCnpjLookupError('CNPJ deve ter 14 dígitos');
      return;
    }

    setCnpjLookupLoading(true);
    setCnpjLookupError(null);
    setCnpjLookupSuccess(null);

    try {
      console.log('🔍 Looking up CNPJ for provider:', cleanCnpj.replace(/\d/g, '*'));
      
      // Use company service for CNPJ lookup (same API endpoint)
      const companyData = await companyService.lookupCNPJ(cleanCnpj);
      console.log('✅ CNPJ lookup successful:', companyData);

      // Fill form with the received data
      if (companyData.legalName) setValue('legalName', companyData.legalName);
      if (companyData.tradeName) setValue('tradeName', companyData.tradeName);
      if (companyData.zipCode) setValue('zipCode', formatCEP(companyData.zipCode));
      if (companyData.state) setValue('state', companyData.state);
      if (companyData.city) setValue('city', companyData.city);
      if (companyData.neighborhood) setValue('neighborhood', companyData.neighborhood);
      if (companyData.address) setValue('address', companyData.address);
      if (companyData.addressNumber) setValue('addressNumber', companyData.addressNumber);

      // Show success message
      setCnpjLookupSuccess(`Dados do prestador "${companyData.legalName}" foram preenchidos automaticamente!`);
      console.log('📝 Form fields filled successfully');
      
    } catch (err: any) {
      console.error('❌ CNPJ lookup error:', err);
      
      let errorMessage = 'Erro ao consultar CNPJ';
      
      if (err.response?.status === 401) {
        errorMessage = 'Usuário não autenticado. Faça login novamente.';
      } else if (err.response?.status === 403) {
        errorMessage = 'Sem permissão para consultar CNPJ.';
      } else if (err.response?.status === 404) {
        errorMessage = 'CNPJ não encontrado ou inválido.';
      } else if (err.response?.data?.error) {
        errorMessage = err.response.data.error;
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setCnpjLookupError(errorMessage);
    } finally {
      setCnpjLookupLoading(false);
    }
  };

  // Document field handlers
  const handleDocFieldChange = (type: ProviderDocumentType, field: 'documentNumber' | 'validityDate', value: string) => {
    setDocState(prev => ({
      ...prev,
      [type]: { ...prev[type], [field]: value },
    }));
  };

  const handleDocFileSelect = async (type: ProviderDocumentType, file: File) => {
    if (providerId) {
      // Edit mode: upload immediately
      setDocState(prev => ({ ...prev, [type]: { ...prev[type], uploading: true } }));
      try {
        await providerService.uploadDocumentFile(providerId, type, file);
        setDocState(prev => ({
          ...prev,
          [type]: { ...prev[type], fileName: file.name, uploading: false },
        }));
      } catch {
        setDocState(prev => ({ ...prev, [type]: { ...prev[type], uploading: false } }));
      }
    } else {
      // Create mode: store file for later upload
      setDocState(prev => ({
        ...prev,
        [type]: { ...prev[type], fileName: file.name, file },
      }));
    }
  };

  const handleDocFileDelete = async (type: ProviderDocumentType) => {
    if (providerId) {
      try {
        await providerService.deleteDocumentFile(providerId, type);
      } catch {
        // ignore
      }
    }
    setDocState(prev => ({
      ...prev,
      [type]: { ...prev[type], fileName: '', file: undefined },
    }));
  };

  // Handle form submission - remove masks before sending
  const handleFormSubmit = async (data: CreateProviderRequest) => {
    // Validate all tabs before final submit
    const isValid = await validateCurrentTab();
    if (!isValid) return;

    // Build documents array from docState
    const documents = Object.entries(docState)
      .filter(([, v]) => v.documentNumber || v.validityDate)
      .map(([type, v]) => ({
        documentType: type as ProviderDocumentType,
        documentNumber: v.documentNumber || undefined,
        validityDate: v.validityDate || undefined,
      }));

    // Build pending files for create mode
    const pendingFiles = Object.entries(docState)
      .filter(([, v]) => v.file)
      .map(([type, v]) => ({
        documentType: type as ProviderDocumentType,
        file: v.file!,
      }));

    const processedData = {
      ...data,
      cnpj: removeCNPJMask(data.cnpj ?? ''),
      zipCode: data.zipCode ? removeCEPMask(data.zipCode) : undefined,
      documents,
      pendingFiles,
    };
    onSubmit(processedData);
  };

  // Handle continue/submit button click
  const handleContinue = async () => {
    if (currentTab === TAB_STEPS.length - 1) {
      // Last tab - submit the form
      await handleSubmit(handleFormSubmit)();
    } else {
      // Not last tab - go to next tab
      await handleNext();
    }
  };

  return (
    <Box sx={{ mt: 1 }}>
      {ehClinicaProspectada && (
        <Box sx={{ mb: 3 }}>
          <PrerequisiteBlock
            title="Esta clínica veio da integração e ainda não é um prestador"
            reason="Ela não aparece em agendamentos, exames nem vínculos com empresa. Falta o CNPJ e o tipo de prestador para que passe a aparecer."
          />
        </Box>
      )}
      {/* Stepper */}
      <Stepper activeStep={currentTab} sx={{ mb: 4 }}>
        {TAB_STEPS.map((step) => (
          <Step key={step.key}>
            <StepLabel>{step.label}</StepLabel>
          </Step>
        ))}
      </Stepper>
      {cnpjLookupSuccess && (
        <Alert
          severity="success"
          sx={{ mb: 3 }}
          onClose={() => setCnpjLookupSuccess(null)}
        >
          {cnpjLookupSuccess}
        </Alert>
      )}
      {/* Tab: Dados Principais */}
      {TAB_STEPS[currentTab]?.key === 'main' && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>Dados Principais</Typography>
          <Grid container spacing={2}>
            <Grid
              size={{
                xs: 12,
                md: 6
              }}>
              <Controller
                name="legalName"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Razão Social"
                    fullWidth
                    required
                    slotProps={{ inputLabel: { shrink: !!field.value } }}
                    error={!!errors.legalName}
                    helperText={errors.legalName?.message}
                  />
                )}
              />
            </Grid>
            <Grid
              size={{
                xs: 12,
                md: 6
              }}>
              <Controller
                name="cnpj"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="CNPJ"
                    fullWidth
                    required={!ehClinicaProspectada}
                    disabled={cnpjImutavel}
                    placeholder="XX.XXX.XXX/XXXX-XX"
                    inputProps={{ maxLength: 18 }}
                    error={!!errors.cnpj || !!cnpjLookupError}
                    helperText={
                      errors.cnpj?.message ||
                      cnpjLookupError ||
                      (ehClinicaProspectada
                        ? 'É o que transforma esta clínica em prestador.'
                        : cnpjImutavel
                          ? 'O CNPJ não muda depois que o prestador está cadastrado.'
                          : undefined)
                    }
                    onChange={(e) => {
                      const formatted = formatCNPJ(e.target.value);
                      field.onChange(formatted);
                      // Clear lookup messages when user types
                      if (cnpjLookupError) setCnpjLookupError(null);
                      if (cnpjLookupSuccess) setCnpjLookupSuccess(null);
                    }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={handleCNPJLookup}
                            disabled={cnpjLookupLoading || !field.value || cnpjImutavel}
                            size="small"
                            title="Buscar dados pelo CNPJ"
                          >
                            {cnpjLookupLoading ? (
                              <CircularProgress size={20} />
                            ) : (
                              <SearchIcon />
                            )}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
              />
            </Grid>
            <Grid
              size={{
                xs: 12,
                md: 6
              }}>
              <Controller
                name="providerType"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth required error={!!errors.providerType}>
                    <InputLabel>Tipo de Prestador</InputLabel>
                    <Select {...field} label="Tipo de Prestador">
                      {Object.values(ProviderType).map((type) => (
                        <MenuItem key={type} value={type}>
                          {type}
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.providerType && <FormHelperText>{errors.providerType?.message}</FormHelperText>}
                  </FormControl>
                )}
              />
            </Grid>
            <Grid
              size={{
                xs: 12,
                md: 6
              }}>
              <Controller
                name="tradeName"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Nome Fantasia"
                    fullWidth
                    slotProps={{ inputLabel: { shrink: !!field.value } }}
                    error={!!errors.tradeName}
                    helperText={errors.tradeName?.message}
                  />
                )}
              />
            </Grid>
            <Grid
              size={{
                xs: 12,
                md: 6
              }}>
              <Controller
                name="status"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth error={!!errors.status}>
                    <InputLabel>Status Operacional</InputLabel>
                    <Select {...field} label="Status Operacional">
                      {Object.values(ProviderStatus).map((status) => (
                        <MenuItem key={status} value={status}>
                          {status}
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.status && <FormHelperText>{errors.status?.message}</FormHelperText>}
                  </FormControl>
                )}
              />
            </Grid>
            <Grid
              size={{
                xs: 12,
                md: 6
              }}>
              <Controller
                name="serviceType"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth required error={!!errors.serviceType}>
                    <InputLabel>Tipo de Atendimento</InputLabel>
                    <Select {...field} label="Tipo de Atendimento">
                      {Object.values(ServiceType).map((type) => (
                        <MenuItem key={type} value={type}>{type}</MenuItem>
                      ))}
                    </Select>
                    {errors.serviceType && <FormHelperText>{errors.serviceType?.message}</FormHelperText>}
                  </FormControl>
                )}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Controller
                name="providerNotice"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Aviso do Prestador"
                    fullWidth
                    multiline
                    rows={4}
                    placeholder="Ex: Horários de atendimento, regras, observações..."
                    error={!!errors.providerNotice}
                    helperText={errors.providerNotice?.message}
                  />
                )}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>
      )}
      {/* Tab: Configuração de Atendimento */}
      {TAB_STEPS[currentTab]?.key === 'attendance' && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>Configuração de Atendimento</Typography>
          {serviceType === ServiceType.AGENDA_CLINICA ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }}>
              {providerId ? (
                <>
                  <Button
                    variant="contained"
                    fullWidth
                    size="large"
                    startIcon={<CalendarIcon />}
                    onClick={() => setWeeklyScheduleOpen(true)}
                    sx={{ maxWidth: 480 }}
                  >
                    Configurar Horários por Dia da Semana
                  </Button>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                    Configure horários, duração de atendimento e capacidade para cada dia da semana
                  </Typography>
                </>
              ) : (
                <Typography color="text.secondary">
                  Salve o prestador para configurar os horários por dia da semana.
                </Typography>
              )}
            </Box>
          ) : (
            <Grid container spacing={2}>
              <Grid size={12}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1 }}>
                  Configuração de Capacidade — Manhã
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <Controller
                  name="morningStartTime"
                  control={control}
                  render={({ field }) => (
                    <TextField {...field} label="Início Manhã" fullWidth placeholder="07:00" />
                  )}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <Controller
                  name="morningEndTime"
                  control={control}
                  render={({ field }) => (
                    <TextField {...field} label="Fim Manhã" fullWidth placeholder="12:00" />
                  )}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <Controller
                  name="morningCapacity"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Capacidade Manhã"
                      fullWidth
                      type="number"
                      placeholder="20"
                      onChange={(e) => field.onChange(e.target.value ? Number(e.target.value) : undefined)}
                    />
                  )}
                />
              </Grid>
              <Grid size={12}>
                <Typography variant="subtitle2" color="text.secondary">
                  Configuração de Capacidade — Tarde
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <Controller
                  name="afternoonStartTime"
                  control={control}
                  render={({ field }) => (
                    <TextField {...field} label="Início Tarde" fullWidth placeholder="13:00" />
                  )}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <Controller
                  name="afternoonEndTime"
                  control={control}
                  render={({ field }) => (
                    <TextField {...field} label="Fim Tarde" fullWidth placeholder="18:00" />
                  )}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <Controller
                  name="afternoonCapacity"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Capacidade Tarde"
                      fullWidth
                      type="number"
                      placeholder="15"
                      onChange={(e) => field.onChange(e.target.value ? Number(e.target.value) : undefined)}
                    />
                  )}
                />
              </Grid>
            </Grid>
          )}
        </CardContent>
      </Card>
      )}

      {/* Weekly Schedule Modal */}
      {providerId && (
        <ProviderWeeklyScheduleModal
          open={weeklyScheduleOpen}
          onClose={(slotConfig) => {
            setWeeklyScheduleOpen(false);
            if (slotConfig) {
              if (slotConfig.defaultSlotDuration != null) setValue('defaultSlotDuration', slotConfig.defaultSlotDuration);
              if (slotConfig.concurrentCapacity != null) setValue('concurrentCapacity', slotConfig.concurrentCapacity);
            }
          }}
          providerId={providerId}
          providerName={initialData?.tradeName || initialData?.legalName}
          defaultMorningStart={initialData?.morningStartTime}
          defaultMorningEnd={initialData?.morningEndTime}
          defaultAfternoonStart={initialData?.afternoonStartTime}
          defaultAfternoonEnd={initialData?.afternoonEndTime}
          initialSlotDuration={initialData?.defaultSlotDuration}
          initialConcurrentCapacity={initialData?.concurrentCapacity}
        />
      )}

      {/* Tab: Endereço */}
      {TAB_STEPS[currentTab]?.key === 'address' && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>Endereço</Typography>
          <Grid container spacing={2}>
            <Grid
              size={{
                xs: 12,
                md: 3
              }}>
              <Controller
                name="zipCode"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="CEP"
                    fullWidth
                    placeholder="XXXXX-XXX"
                    inputProps={{ maxLength: 9 }}
                    slotProps={{ inputLabel: { shrink: !!field.value } }}
                    error={!!errors.zipCode}
                    helperText={errors.zipCode?.message}
                    onChange={(e) => {
                      const formatted = formatCEP(e.target.value);
                      field.onChange(formatted);
                    }}
                  />
                )}
              />
            </Grid>
            <Grid
              size={{
                xs: 12,
                md: 6
              }}>
              <Controller
                name="address"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Logradouro"
                    fullWidth
                    slotProps={{ inputLabel: { shrink: !!field.value } }}
                    error={!!errors.address}
                    helperText={errors.address?.message}
                  />
                )}
              />
            </Grid>
            <Grid
              size={{
                xs: 12,
                md: 3
              }}>
              <Controller
                name="addressNumber"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Número"
                    fullWidth
                    slotProps={{ inputLabel: { shrink: !!field.value } }}
                    error={!!errors.addressNumber}
                    helperText={errors.addressNumber?.message}
                  />
                )}
              />
            </Grid>
            <Grid
              size={{
                xs: 12,
                md: 4
              }}>
              <Controller
                name="neighborhood"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Bairro"
                    fullWidth
                    slotProps={{ inputLabel: { shrink: !!field.value } }}
                    error={!!errors.neighborhood}
                    helperText={errors.neighborhood?.message}
                  />
                )}
              />
            </Grid>
            <Grid
              size={{
                xs: 12,
                md: 4
              }}>
              <Controller
                name="city"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Cidade"
                    fullWidth
                    slotProps={{ inputLabel: { shrink: !!field.value } }}
                    error={!!errors.city}
                    helperText={errors.city?.message}
                  />
                )}
              />
            </Grid>
            <Grid
              size={{
                xs: 12,
                md: 4
              }}>
              <Controller
                name="state"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="UF"
                    fullWidth
                    inputProps={{ maxLength: 2 }}
                    slotProps={{ inputLabel: { shrink: !!field.value } }}
                    error={!!errors.state}
                    helperText={errors.state?.message}
                  />
                )}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>
      )}
      {/* Tab: Contatos */}
      {TAB_STEPS[currentTab]?.key === 'contacts' && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>Contatos</Typography>
          <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>Recepção</Typography>
          <Grid container spacing={2}>
            <Grid
              size={{
                xs: 12,
                md: 4
              }}>
              <Controller
                name="receptionContactName"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Nome do Contato"
                    fullWidth
                    error={!!errors.receptionContactName}
                    helperText={errors.receptionContactName?.message}
                  />
                )}
              />
            </Grid>
            <Grid
              size={{
                xs: 12,
                md: 4
              }}>
              <Controller
                name="receptionEmail"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="E-mail"
                    fullWidth
                    error={!!errors.receptionEmail}
                    helperText={errors.receptionEmail?.message}
                  />
                )}
              />
            </Grid>
            <Grid
              size={{
                xs: 12,
                md: 4
              }}>
              <Controller
                name="receptionPhone"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Telefone"
                    fullWidth
                    error={!!errors.receptionPhone}
                    helperText={errors.receptionPhone?.message}
                  />
                )}
              />
            </Grid>
          </Grid>

          <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>Laudos</Typography>
          <Grid container spacing={2}>
            <Grid
              size={{
                xs: 12,
                md: 4
              }}>
              <Controller
                name="reportContactName"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Nome do Contato"
                    fullWidth
                    error={!!errors.reportContactName}
                    helperText={errors.reportContactName?.message}
                  />
                )}
              />
            </Grid>
            <Grid
              size={{
                xs: 12,
                md: 4
              }}>
              <Controller
                name="reportEmail"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="E-mail"
                    fullWidth
                    error={!!errors.reportEmail}
                    helperText={errors.reportEmail?.message}
                  />
                )}
              />
            </Grid>
            <Grid
              size={{
                xs: 12,
                md: 4
              }}>
              <Controller
                name="reportPhone"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Telefone"
                    fullWidth
                    error={!!errors.reportPhone}
                    helperText={errors.reportPhone?.message}
                  />
                )}
              />
            </Grid>
          </Grid>

          <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>Administrativo</Typography>
          <Grid container spacing={2}>
            <Grid
              size={{
                xs: 12,
                md: 4
              }}>
              <Controller
                name="contactName"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Nome do Contato"
                    fullWidth
                    error={!!errors.contactName}
                    helperText={errors.contactName?.message}
                  />
                )}
              />
            </Grid>
            <Grid
              size={{
                xs: 12,
                md: 4
              }}>
              <Controller
                name="contactEmail"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="E-mail"
                    fullWidth
                    error={!!errors.contactEmail}
                    helperText={errors.contactEmail?.message}
                  />
                )}
              />
            </Grid>
            <Grid
              size={{
                xs: 12,
                md: 4
              }}>
              <Controller
                name="contactPhone"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Telefone"
                    fullWidth
                    error={!!errors.contactPhone}
                    helperText={errors.contactPhone?.message}
                  />
                )}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>
      )}
      {/* Tab: Dados Financeiros */}
      {TAB_STEPS[currentTab]?.key === 'financial' && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>Dados Financeiros</Typography>
          <Grid container spacing={2}>
            {/* Linha 1: PIX | Banco | Agência | Conta */}
            <Grid
              size={{
                xs: 12,
                md: 3
              }}>
              <Controller
                name="pix"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="PIX"
                    fullWidth
                    error={!!errors.pix}
                    helperText={errors.pix?.message}
                  />
                )}
              />
            </Grid>
            <Grid
              size={{
                xs: 12,
                md: 3
              }}>
              <Controller
                name="bankName"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Nome do Banco"
                    fullWidth
                    error={!!errors.bankName}
                    helperText={errors.bankName?.message}
                  />
                )}
              />
            </Grid>
            <Grid
              size={{
                xs: 12,
                md: 3
              }}>
              <Controller
                name="bankAgency"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Agência"
                    fullWidth
                    error={!!errors.bankAgency}
                    helperText={errors.bankAgency?.message}
                  />
                )}
              />
            </Grid>
            <Grid
              size={{
                xs: 12,
                md: 3
              }}>
              <Controller
                name="bankAccount"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Conta"
                    fullWidth
                    error={!!errors.bankAccount}
                    helperText={errors.bankAccount?.message}
                  />
                )}
              />
            </Grid>

            {/* Linha 2: Pagamento | Data Contratação | Data Encerramento | Motivo Encerramento */}
            <Grid
              size={{
                xs: 12,
                md: 3
              }}>
              <Controller
                name="paymentMethod"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    value={field.value ?? ''}
                    select
                    label="Forma de Pagamento"
                    fullWidth
                    error={!!errors.paymentMethod}
                    helperText={errors.paymentMethod?.message}
                  >
                    <MenuItem value="">Selecione...</MenuItem>
                    {['Boleto', 'Crédito', 'Débito', 'Depósito', 'PIX', 'Transferência', 'Outros'].map((m) => (
                      <MenuItem key={m} value={m}>{m}</MenuItem>
                    ))}
                  </TextField>
                )}
              />
            </Grid>
            <Grid
              size={{
                xs: 12,
                md: 3
              }}>
              <Controller
                name="contractStartDate"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Data da Contratação"
                    type="date"
                    fullWidth
                    slotProps={{ inputLabel: { shrink: true } }}
                    error={!!errors.contractStartDate}
                    helperText={errors.contractStartDate?.message}
                  />
                )}
              />
            </Grid>
            <Grid
              size={{
                xs: 12,
                md: 3
              }}>
              <Controller
                name="contractEndDate"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Data do Encerramento"
                    type="date"
                    fullWidth
                    slotProps={{ inputLabel: { shrink: true } }}
                    error={!!errors.contractEndDate}
                    helperText={errors.contractEndDate?.message}
                  />
                )}
              />
            </Grid>
            <Grid
              size={{
                xs: 12,
                md: 3
              }}>
              <Controller
                name="terminationReason"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Motivo do Encerramento"
                    fullWidth
                    error={!!errors.terminationReason}
                    helperText={errors.terminationReason?.message}
                  />
                )}
              />
            </Grid>

            {/* Linha 3: Data Fechamento | Data Pagamento */}
            <Grid
              size={{
                xs: 12,
                md: 3
              }}>
              <Controller
                name="closingDate"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Data do Fechamento"
                    type="date"
                    fullWidth
                    slotProps={{ inputLabel: { shrink: true } }}
                    error={!!errors.closingDate}
                    helperText={errors.closingDate?.message}
                  />
                )}
              />
            </Grid>
            <Grid
              size={{
                xs: 12,
                md: 3
              }}>
              <Controller
                name="paymentDate"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Data do Pagamento"
                    type="date"
                    fullWidth
                    slotProps={{ inputLabel: { shrink: true } }}
                    error={!!errors.paymentDate}
                    helperText={errors.paymentDate?.message}
                  />
                )}
              />
            </Grid>
            {/* Reajuste de valores dos exames — aplicação do percentual a ser desenvolvida posteriormente */}
            <Grid
              size={{
                xs: 12,
                md: 3
              }}>
              <Controller
                name="priceAdjustmentDate"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    value={field.value ?? ''}
                    label="Data do Reajuste"
                    type="date"
                    fullWidth
                    slotProps={{ inputLabel: { shrink: true } }}
                    error={!!errors.priceAdjustmentDate}
                    helperText={errors.priceAdjustmentDate?.message}
                  />
                )}
              />
            </Grid>
            <Grid
              size={{
                xs: 12,
                md: 3
              }}>
              <Controller
                name="priceAdjustmentPercentage"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    value={field.value ?? ''}
                    label="Reajuste de Exames (%)"
                    type="number"
                    fullWidth
                    placeholder="0,00"
                    onChange={(e) => field.onChange(e.target.value ? Number(e.target.value) : undefined)}
                    error={!!errors.priceAdjustmentPercentage}
                    helperText={errors.priceAdjustmentPercentage?.message}
                  />
                )}
              />
            </Grid>
          </Grid>

          {/* Contato Financeiro */}
          <Typography variant="subtitle1" gutterBottom sx={{ mt: 3 }}>Contato Financeiro</Typography>
          <Grid container spacing={2}>
            <Grid
              size={{
                xs: 12,
                md: 4
              }}>
              <Controller
                name="financialContactName"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Nome do Contato"
                    fullWidth
                    error={!!errors.financialContactName}
                    helperText={errors.financialContactName?.message}
                  />
                )}
              />
            </Grid>
            <Grid
              size={{
                xs: 12,
                md: 4
              }}>
              <Controller
                name="financialEmail"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="E-mail Financeiro"
                    fullWidth
                    error={!!errors.financialEmail}
                    helperText={errors.financialEmail?.message}
                  />
                )}
              />
            </Grid>
            <Grid
              size={{
                xs: 12,
                md: 4
              }}>
              <Controller
                name="financialPhone"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Telefone Financeiro"
                    fullWidth
                    error={!!errors.financialPhone}
                    helperText={errors.financialPhone?.message}
                  />
                )}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>
      )}
      {/* Tab: Documentos (Clínica → ANVISA; Ambiental → Certificados) */}
      {TAB_STEPS[currentTab]?.key === 'docs' && (
        <>
          {/* Seção: Documentos do Prestador (apenas Clínica) */}
          {providerType === ProviderType.CLINICA && (
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>Documentos do Prestador</Typography>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 4 }}>
                  <Controller
                    name="responsibleName"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Nome do Responsável"
                        fullWidth
                        error={!!(errors as any).responsibleName}
                        helperText={(errors as any).responsibleName?.message}
                      />
                    )}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                  <Controller
                    name="responsibleCRM"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="CRM do Responsável"
                        fullWidth
                        error={!!(errors as any).responsibleCRM}
                        helperText={(errors as any).responsibleCRM?.message}
                      />
                    )}
                  />
                </Grid>
              </Grid>

              {/* ANVISA License */}
              <Typography variant="subtitle1" gutterBottom sx={{ mt: 3 }}>
                {PROVIDER_DOCUMENT_TYPE_LABELS[ProviderDocumentType.ANVISA_LICENSE]}
              </Typography>
              <Grid container spacing={2} alignItems="center">
                <Grid size={{ xs: 12, md: 4 }}>
                  <TextField
                    label="Nº da Licença"
                    fullWidth
                    value={docState[ProviderDocumentType.ANVISA_LICENSE].documentNumber}
                    onChange={(e) => handleDocFieldChange(ProviderDocumentType.ANVISA_LICENSE, 'documentNumber', e.target.value)}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                  <TextField
                    label="Validade"
                    type="date"
                    fullWidth
                    slotProps={{ inputLabel: { shrink: true } }}
                    value={docState[ProviderDocumentType.ANVISA_LICENSE].validityDate}
                    onChange={(e) => handleDocFieldChange(ProviderDocumentType.ANVISA_LICENSE, 'validityDate', e.target.value)}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {docState[ProviderDocumentType.ANVISA_LICENSE].fileName ? (
                      <>
                        <FileIcon color="primary" />
                        <Typography variant="body2" sx={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {docState[ProviderDocumentType.ANVISA_LICENSE].fileName}
                        </Typography>
                        <IconButton size="small" color="error" onClick={() => handleDocFileDelete(ProviderDocumentType.ANVISA_LICENSE)}>
                          <DeleteIcon />
                        </IconButton>
                      </>
                    ) : (
                      <Button
                        variant="outlined"
                        component="label"
                        startIcon={docState[ProviderDocumentType.ANVISA_LICENSE].uploading ? <CircularProgress size={16} /> : <UploadIcon />}
                        disabled={docState[ProviderDocumentType.ANVISA_LICENSE].uploading}
                        size="small"
                      >
                        Upload
                        <input
                          type="file"
                          hidden
                          accept=".pdf,.png,.jpg,.jpeg,.gif,.bmp,.tiff"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) handleDocFileSelect(ProviderDocumentType.ANVISA_LICENSE, file);
                            e.target.value = '';
                          }}
                        />
                      </Button>
                    )}
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
          )}

          {/* Seção: Documento e Dados de Certificado (apenas Ambiental) */}
          {providerType === ProviderType.AMBIENTAL && (
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>Documento e Dados de Certificado</Typography>
              {CERTIFICATE_TYPES.map((type) => (
                <Box key={type} sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" gutterBottom sx={{ mt: 1 }}>
                    {PROVIDER_DOCUMENT_TYPE_LABELS[type]}
                  </Typography>
                  <Grid container spacing={2} alignItems="center">
                    <Grid size={{ xs: 12, md: 4 }}>
                      <TextField
                        label="Número"
                        fullWidth
                        size="small"
                        value={docState[type].documentNumber}
                        onChange={(e) => handleDocFieldChange(type, 'documentNumber', e.target.value)}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 4 }}>
                      <TextField
                        label="Validade"
                        type="date"
                        fullWidth
                        size="small"
                        slotProps={{ inputLabel: { shrink: true } }}
                        value={docState[type].validityDate}
                        onChange={(e) => handleDocFieldChange(type, 'validityDate', e.target.value)}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 4 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {docState[type].fileName ? (
                          <>
                            <FileIcon color="primary" fontSize="small" />
                            <Typography variant="body2" sx={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {docState[type].fileName}
                            </Typography>
                            <IconButton size="small" color="error" onClick={() => handleDocFileDelete(type)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </>
                        ) : (
                          <Button
                            variant="outlined"
                            component="label"
                            startIcon={docState[type].uploading ? <CircularProgress size={14} /> : <UploadIcon />}
                            disabled={docState[type].uploading}
                            size="small"
                          >
                            Upload
                            <input
                              type="file"
                              hidden
                              accept=".pdf,.png,.jpg,.jpeg,.gif,.bmp,.tiff"
                              onChange={(e) => {
                                const file = e.target.files?.[0];
                                if (file) handleDocFileSelect(type, file);
                                e.target.value = '';
                              }}
                            />
                          </Button>
                        )}
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
              ))}
            </CardContent>
          </Card>
          )}
        </>
      )}
      {/* Navigation Buttons */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3, gap: 2 }}>
        <Button
          variant="outlined"
          onClick={onCancel}
          startIcon={<CancelIcon />}
          disabled={isSubmitting}
        >
          Cancelar
        </Button>

        <Box sx={{ display: 'flex', gap: 2 }}>
          {currentTab > 0 && (
            <Button
              variant="outlined"
              onClick={handleBack}
              startIcon={<BackIcon />}
              disabled={isSubmitting}
            >
              Voltar
            </Button>
          )}
          <Button
            variant="contained"
            color="primary"
            onClick={handleContinue}
            startIcon={
              isSubmitting ? (
                <CircularProgress size={20} color="inherit" />
              ) : currentTab === TAB_STEPS.length - 1 ? (
                <SaveIcon />
              ) : (
                <NextIcon />
              )
            }
            disabled={isSubmitting}
          >
            {currentTab === TAB_STEPS.length - 1
              ? ehClinicaProspectada
                ? 'Completar cadastro'
                : initialData
                  ? 'Salvar Alterações'
                  : 'Cadastrar Prestador'
              : 'Próximo'}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default ProviderForm;
